import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../ui/select";
import { Input } from "../../../ui/input";
import { Checkbox } from "../../../ui/checkbox";
import { Label } from "../../../ui/label";
import { EDSButton } from "../../../eds/EDSButton";
import { EDSDatePicker } from "../../../eds/EDSDatePicker";
import { bookableAreasClosed, VenueConfig } from "../../../../types/venues";

const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = hour.toString().padStart(2, "0");
      const minuteStr = minute.toString().padStart(2, "0");
      options.push(`${hourStr}:${minuteStr}`);
    }
  }
  return options;
};

const generateTimeToOptions = (): string[] => {
  const options = generateTimeOptions();
  options.push("23:59");
  return options;
};

const WEEKDAY_LABELS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" }
] as const;

interface CreateEditClosedAreaProps {
  open: boolean;
  selectedClosedArea?: bookableAreasClosed;
  venueConfig?: VenueConfig | null;
  onSave: (closure: bookableAreasClosed) => void;
  onClose: () => void;
  onDelete?: (closure: bookableAreasClosed) => void;
  showDeleteButton?: boolean;
}

export const CreateEditClosedArea = ({
  open,
  selectedClosedArea,
  venueConfig,
  onSave,
  onClose,
  onDelete,
  showDeleteButton = false
}: CreateEditClosedAreaProps) => {
  const [type, setType] = useState<"areas" | "zone">("areas");
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [dateOrWeekday, setDateOrWeekday] = useState<"date" | "weekday">(
    "weekday"
  );
  const [closeAllDay, setCloseAllDay] = useState<boolean>(false);
  const [weekdays, setWeekdays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [timeFrom, setTimeFrom] = useState<string>("00:00");
  const [timeTo, setTimeTo] = useState<string>("23:59");
  const [dateFromTime, setDateFromTime] = useState<string>("00:00");
  const [dateToTime, setDateToTime] = useState<string>("23:59");
  const [message, setMessage] = useState<string>("");

  const timeFromOptions = generateTimeOptions();
  const timeToOptions = generateTimeToOptions();

  const resetFormToDefaults = () => {
    setType("areas");
    setSelectedAreas([]);
    setSelectedZone("");
    setDateOrWeekday("weekday");
    setCloseAllDay(false);
    setWeekdays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    });
    setDateFrom(undefined);
    setDateTo(undefined);
    setTimeFrom("00:00");
    setTimeTo("23:59");
    setDateFromTime("00:00");
    setDateToTime("23:59");
    setMessage("");
  };

  const isFullDayDateClosure = (closure: bookableAreasClosed): boolean => {
    if (!closure.date_from || !closure.date_to) return false;

    const dateFromObj = new Date(closure.date_from);
    const dateToObj = new Date(closure.date_to);
    return (
      dateFromObj.getHours() === 0 &&
      dateFromObj.getMinutes() === 0 &&
      dateToObj.getHours() === 23 &&
      dateToObj.getMinutes() === 59
    );
  };

  const getWeekdaysFromClosure = (closure: bookableAreasClosed) => {
    return closure.weekdays
      ? {
          monday: closure.weekdays.monday ?? false,
          tuesday: closure.weekdays.tuesday ?? false,
          wednesday: closure.weekdays.wednesday ?? false,
          thursday: closure.weekdays.thursday ?? false,
          friday: closure.weekdays.friday ?? false,
          saturday: closure.weekdays.saturday ?? false,
          sunday: closure.weekdays.sunday ?? false
        }
      : {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        };
  };

  const setDateTimeFromClosure = (
    closure: bookableAreasClosed,
    isDateMode: boolean
  ) => {
    if (isDateMode && closure.date_from) {
      const dateFromObj = new Date(closure.date_from);
      setDateFrom(dateFromObj);
      const hours = dateFromObj.getHours().toString().padStart(2, "0");
      const minutes = dateFromObj.getMinutes().toString().padStart(2, "0");
      setDateFromTime(`${hours}:${minutes}`);
    } else {
      setDateFrom(closure.date_from ? new Date(closure.date_from) : undefined);
      setDateFromTime("00:00");
    }

    if (isDateMode && closure.date_to) {
      const dateToObj = new Date(closure.date_to);
      setDateTo(dateToObj);
      const hours = dateToObj.getHours().toString().padStart(2, "0");
      const minutes = dateToObj.getMinutes().toString().padStart(2, "0");
      setDateToTime(`${hours}:${minutes}`);
    } else {
      setDateTo(closure.date_to ? new Date(closure.date_to) : undefined);
      setDateToTime("23:59");
    }
  };

  const populateFormFromClosure = (closure: bookableAreasClosed) => {
    setType(closure.type);
    setSelectedAreas(closure.areas ?? []);
    setSelectedZone(closure.zone ?? "");

    const isDateMode = closure.date_type !== "weekdays";
    setDateOrWeekday(isDateMode ? "date" : "weekday");

    // Determine if it's a full day closure
    if (closure.date_type === "weekdays") {
      setCloseAllDay(!closure.time_from && !closure.time_to);
    } else {
      setCloseAllDay(isFullDayDateClosure(closure));
    }

    setWeekdays(getWeekdaysFromClosure(closure));
    setDateTimeFromClosure(closure, isDateMode);
    setTimeFrom(closure.time_from ?? "00:00");
    setTimeTo(closure.time_to ?? "23:59");
    setMessage(closure.message ?? "");
  };

  useEffect(() => {
    if (selectedClosedArea) {
      populateFormFromClosure(selectedClosedArea);
    } else {
      resetFormToDefaults();
    }
  }, [selectedClosedArea, open]);

  const handleAreaToggle = (areaId: string, checked: boolean) => {
    setSelectedAreas((prev) =>
      checked ? [...prev, areaId] : prev.filter((id) => id !== areaId)
    );
  };

  const handleWeekdayToggle = (
    day: keyof typeof weekdays,
    checked: boolean
  ) => {
    setWeekdays((prev) => ({ ...prev, [day]: checked }));
  };

  /**
   * Formats a Date object to the API-expected format (Y-m-d H:i:s).
   * Uses dayjs for consistency with CloseAreasModal.tsx.
   */
  const formatDateForApi = (date: Date): string => {
    return dayjs(date).format("YYYY-MM-DD HH:mm:ss");
  };

  const handleSave = () => {
    let dateFromFormatted: string | undefined;
    let dateToFormatted: string | undefined;

    if (dateOrWeekday === "date" && dateFrom && dateTo) {
      if (closeAllDay) {
        // For all day closures, set to 00:00 and 23:59
        const dateFromCombined = new Date(dateFrom);
        dateFromCombined.setHours(0, 0, 0, 0);
        dateFromFormatted = formatDateForApi(dateFromCombined);

        const dateToCombined = new Date(dateTo);
        dateToCombined.setHours(23, 59, 0, 0);
        dateToFormatted = formatDateForApi(dateToCombined);
      } else {
        // Use the specific times
        const [fromHours, fromMinutes] = dateFromTime.split(":").map(Number);
        const dateFromCombined = new Date(dateFrom);
        dateFromCombined.setHours(fromHours, fromMinutes, 0, 0);
        dateFromFormatted = formatDateForApi(dateFromCombined);

        const [toHours, toMinutes] = dateToTime.split(":").map(Number);
        const dateToCombined = new Date(dateTo);
        dateToCombined.setHours(toHours, toMinutes, 0, 0);
        dateToFormatted = formatDateForApi(dateToCombined);
      }
    }

    const closure: bookableAreasClosed = {
      id:
        selectedClosedArea?.id ??
        `closure_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      type,
      ...(type === "areas" && { areas: selectedAreas }),
      ...(type === "zone" && { zone: selectedZone }),
      ...(dateOrWeekday === "weekday" && {
        date_type: "weekdays",
        weekdays,
        ...(!closeAllDay && {
          time_from: timeFrom,
          time_to: timeTo
        })
      }),
      ...(dateOrWeekday === "date" && {
        date_from: dateFromFormatted,
        date_to: dateToFormatted
      }),
      message
    };

    onSave(closure);
    onClose();
  };

  const handleDelete = () => {
    if (selectedClosedArea && onDelete) {
      onDelete(selectedClosedArea);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="CreateEditClosedArea sm:!max-w-670px] lg:!max-w-[670px] !min-w-0 max-h-[90vh] overflow-hidden flex flex-col p-6">
        <DialogHeader className="mb-4 shrink-0">
          <DialogTitle className="text-xl font-semibold text-[var(--primaryColor)]">
            Closed Area Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-visible flex flex-col gap-3 md:gap-4 pr-0 md:pr-6 pl-1 py-1 relative z-[1]">
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="type-select"
              className="text-sm font-semibold text-foreground"
            >
              Type of area
            </Label>
            <Select
              value={type}
              onValueChange={(value: "areas" | "zone") => setType(value)}
            >
              <SelectTrigger id="type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                usePortal={false}
                className="z-[9999]"
                position="popper"
                sideOffset={4}
              >
                <SelectItem value="areas">Table/area</SelectItem>
                <SelectItem value="zone">Zone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "areas" && (
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="areas-container"
                className="text-sm font-semibold text-foreground"
              >
                Areas to close
              </Label>
              <div
                id="areas-container"
                className="border border-border rounded-md p-3 max-h-40 overflow-y-auto flex flex-col gap-2"
              >
                {venueConfig?.bookable_areas?.map((area) => (
                  <div key={area.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`area-${area.id}`}
                      checked={selectedAreas.includes(area.id)}
                      onCheckedChange={(checked) =>
                        handleAreaToggle(area.id, checked as boolean)
                      }
                      className="border h-6 w-6 border-solid border-[hsla(180,70%,45%,0.2)]"
                    />
                    <Label htmlFor={`area-${area.id}`}>
                      {area.name ?? area.id}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {type === "zone" && (
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="zone-select"
                className="text-sm font-semibold text-foreground"
              >
                Zone
              </Label>
              <Select value={selectedZone} onValueChange={setSelectedZone}>
                <SelectTrigger id="zone-select">
                  <SelectValue placeholder="Select zone" />
                </SelectTrigger>
                <SelectContent
                  usePortal={false}
                  className="z-[9999]"
                  position="popper"
                  sideOffset={4}
                >
                  {venueConfig?.bookable_area_zones?.map((zone) => (
                    <SelectItem key={zone.id} value={zone.id}>
                      {zone.name ?? zone.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1 flex flex-col gap-2">
                <Label
                  htmlFor="date-weekday-select"
                  className="text-sm font-semibold text-foreground"
                >
                  Close by date or weekday
                </Label>
                <Select
                  value={dateOrWeekday}
                  onValueChange={(value: "date" | "weekday") =>
                    setDateOrWeekday(value)
                  }
                >
                  <SelectTrigger id="date-weekday-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent
                    usePortal={false}
                    className="z-[9999]"
                    position="popper"
                    sideOffset={4}
                  >
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="weekday">Weekday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="pb-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="close-all-day"
                    checked={closeAllDay}
                    onCheckedChange={(checked) =>
                      setCloseAllDay(checked as boolean)
                    }
                    className="border h-6 w-6 border-solid border-[hsla(180,70%,45%,0.2)]"
                  />
                  <Label htmlFor="close-all-day">Close all day</Label>
                </div>
              </div>
            </div>
          </div>

          {dateOrWeekday === "date" && (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-visible">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Date from
                  </Label>
                  <div className="flex gap-2">
                    <div className="date-picker-175">
                      <EDSDatePicker
                        label=""
                        date={dateFrom}
                        onSelect={setDateFrom}
                        placeholder="Select start date"
                      />
                    </div>
                    {!closeAllDay && (
                      <div className="flex-1">
                        <Select
                          value={dateFromTime}
                          onValueChange={setDateFromTime}
                        >
                          <SelectTrigger
                            id="date-from-time-select"
                            aria-label="Select start time"
                            className="h-10"
                            style={{ height: "36px", width: "105px" }}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent
                            usePortal={false}
                            className="z-[9999]"
                            position="popper"
                            sideOffset={4}
                          >
                            {timeFromOptions.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Date to
                  </Label>
                  <div className="flex gap-2">
                    <div className="date-picker-175">
                      <EDSDatePicker
                        label=""
                        date={dateTo}
                        onSelect={setDateTo}
                        placeholder="Select end date"
                      />
                    </div>
                    {!closeAllDay && (
                      <div className="flex-1">
                        <Select
                          value={dateToTime}
                          onValueChange={setDateToTime}
                        >
                          <SelectTrigger
                            id="date-to-time-select"
                            aria-label="Select end time"
                            className="h-10"
                            style={{ height: "36px", width: "105px" }}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent
                            usePortal={false}
                            className="z-[9999]"
                            position="popper"
                            sideOffset={4}
                          >
                            {timeToOptions.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {dateOrWeekday === "weekday" && (
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="weekdays-grid"
                className="text-sm font-semibold text-foreground"
              >
                Select weekdays
              </Label>
              <div id="weekdays-grid" className="flex flex-wrap gap-3 md:gap-4">
                {WEEKDAY_LABELS.map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`weekday-${key}`}
                      checked={weekdays[key]}
                      onCheckedChange={(checked) =>
                        handleWeekdayToggle(key, checked as boolean)
                      }
                      className="border h-6 w-6 border-solid border-[hsla(180,70%,45%,0.2)]"
                    />
                    <Label htmlFor={`weekday-${key}`}>{label}</Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!closeAllDay && dateOrWeekday === "weekday" && (
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="time-grid"
                className="text-sm font-semibold text-foreground"
              >
                Time
              </Label>
              <div
                id="time-grid"
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div className="flex flex-col gap-1">
                  <Label
                    htmlFor="time-from-select"
                    className="text-xs text-muted-foreground font-normal"
                  >
                    From
                  </Label>
                  <Select value={timeFrom} onValueChange={setTimeFrom}>
                    <SelectTrigger id="time-from-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent
                      usePortal={false}
                      className="z-[9999]"
                      position="popper"
                      sideOffset={4}
                    >
                      {timeFromOptions.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label
                    htmlFor="time-to-select"
                    className="text-xs text-muted-foreground font-normal"
                  >
                    To
                  </Label>
                  <Select value={timeTo} onValueChange={setTimeTo}>
                    <SelectTrigger id="time-to-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent
                      usePortal={false}
                      className="z-[9999]"
                      position="popper"
                      sideOffset={4}
                    >
                      {timeToOptions.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="admin-message-input"
              className="text-sm font-semibold text-foreground"
            >
              Admin message
            </Label>
            <Input
              id="admin-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter message..."
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between pt-4 pb-6 mt-auto border-t border-border shrink-0">
          <div>
            {showDeleteButton && selectedClosedArea && (
              <EDSButton
                variant="outline"
                onClick={handleDelete}
                style={{ height: "37px" }}
              >
                Delete
              </EDSButton>
            )}
          </div>
          <div className="flex gap-4">
            <EDSButton
              variant="outline"
              onClick={onClose}
              style={{ height: "37px" }}
            >
              Cancel
            </EDSButton>
            <EDSButton onClick={handleSave}>Save</EDSButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
